/**
 * CookieBanner — banner LGPD para opt-in granular de cookies.
 *
 * Aparece no primeiro acesso. Persiste em localStorage o JSON com a decisão
 * por categoria. NAO usa cookies de tracking até que o usuário aceite.
 *
 * Categorias:
 *   - essenciais: sempre ativo (sessao, login, RLS)
 *   - analytics: PostHog/GA (opcional)
 *   - marketing: pixels de conversao (opcional)
 *
 * Compativel com a LGPD por:
 *   - opt-in granular por finalidade
 *   - fácil retirar consentimento (botao "Cookies" no rodape)
 *   - registro de versão do termo aceito
 *
 * Quando integrar PostHog/Sentry replay, leitor deve checar
 * `getConsentimentoCookies()` antes de iniciar.
 */
import { Cookie, ShieldCheck, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from './ui/button';

const STORAGE_KEY = 'octawash:cookies:v1';
const VERSAO = '1.0.0';

export interface ConsentimentoCookies {
  versao: string;
  essenciais: true;
  analytics: boolean;
  marketing: boolean;
  dta_aceite: string;
}

export function getConsentimentoCookies(): ConsentimentoCookies | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentimentoCookies;
    if (parsed.versao !== VERSAO) return null;
    return parsed;
  } catch {
    return null;
  }
}

function salvar(decisoes: { analytics: boolean; marketing: boolean }): void {
  const c: ConsentimentoCookies = {
    versao: VERSAO,
    essenciais: true,
    analytics: decisoes.analytics,
    marketing: decisoes.marketing,
    dta_aceite: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
}

interface Props {
  forcarReabrir?: boolean;
  onFechar?: () => void;
}

export function CookieBanner({ forcarReabrir = false, onFechar }: Props) {
  const [aberto, setAberto] = useState(false);
  const [detalhes, setDetalhes] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (forcarReabrir) {
      const atual = getConsentimentoCookies();
      if (atual) {
        setAnalytics(atual.analytics);
        setMarketing(atual.marketing);
      }
      setAberto(true);
      return;
    }
    setAberto(getConsentimentoCookies() == null);
  }, [forcarReabrir]);

  function fechar() {
    setAberto(false);
    onFechar?.();
  }

  function aceitarTodos() {
    salvar({ analytics: true, marketing: true });
    fechar();
  }

  function rejeitarOpcionais() {
    salvar({ analytics: false, marketing: false });
    fechar();
  }

  function salvarPersonalizado() {
    salvar({ analytics, marketing });
    fechar();
  }

  if (!aberto) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-titulo"
      className="fixed inset-x-0 bottom-0 z-50 p-4 sm:bottom-4 sm:left-4 sm:max-w-md sm:rounded-lg"
    >
      <div className="bg-background border-border ring-foreground/5 rounded-lg border p-5 shadow-lg ring-1">
        <div className="flex items-start gap-3">
          <Cookie className="text-primary mt-0.5 h-5 w-5 flex-shrink-0" aria-hidden />
          <div className="flex-1">
            <h2 id="cookie-titulo" className="text-base font-semibold">
              Cookies e privacidade
            </h2>
            <p className="text-muted-foreground mt-1 text-sm">
              Usamos cookies essenciais para o funcionamento da plataforma. Os demais (analytics e
              marketing) so sao ativados com seu consentimento granular conforme a LGPD.
            </p>

            {detalhes && (
              <div className="mt-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium">Essenciais</p>
                    <p className="text-muted-foreground text-xs">
                      Sessao, login, idioma. Sempre ativos.
                    </p>
                  </div>
                  <span className="bg-muted text-muted-foreground rounded px-2 py-0.5 text-xs">
                    Sempre
                  </span>
                </div>
                <label className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium">Analytics</p>
                    <p className="text-muted-foreground text-xs">
                      Medir funil de conversao e engajamento (PostHog).
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={analytics}
                    onChange={(e) => setAnalytics(e.target.checked)}
                    className="mt-1 h-4 w-4"
                    aria-label="Analytics"
                  />
                </label>
                <label className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium">Marketing</p>
                    <p className="text-muted-foreground text-xs">
                      Pixels de campanha e remarketing (opcional).
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={marketing}
                    onChange={(e) => setMarketing(e.target.checked)}
                    className="mt-1 h-4 w-4"
                    aria-label="Marketing"
                  />
                </label>
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              {!detalhes ? (
                <>
                  <Button size="sm" onClick={aceitarTodos}>
                    Aceitar todos
                  </Button>
                  <Button size="sm" variant="outline" onClick={rejeitarOpcionais}>
                    So essenciais
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setDetalhes(true)}>
                    Personalizar
                  </Button>
                </>
              ) : (
                <>
                  <Button size="sm" onClick={salvarPersonalizado}>
                    Salvar escolhas
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setDetalhes(false)}>
                    Voltar
                  </Button>
                </>
              )}
            </div>
            <p className="text-muted-foreground mt-3 flex items-center gap-1 text-xs">
              <ShieldCheck className="h-3 w-3" />
              Voce pode mudar isto a qualquer momento em &quot;Configuracoes &gt; Cookies&quot;.
            </p>
          </div>
          {!forcarReabrir && (
            <button
              type="button"
              onClick={rejeitarOpcionais}
              aria-label="Fechar (rejeitar opcionais)"
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
